import { useMutation } from '@apollo/client/react';
import { FormikInput } from '../../../components/formik/FormInput';
import { FormikSelect } from '../../../components/formik/FormSelect';
import { Option } from '../../../components/inputs/Select';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { UserContext } from '../../../contexts/UserContext';
import { PROJECTS, PROJECT_COPY_TO_ORGANISATION } from '../../../graphql/project';
import { T, useT } from '../../../translation/src';
import { Project, ProjectCopyToOrganisationMutation, ProjectCopyToOrganisationMutationVariables } from '../../../types/graphqlTypes';
import { FormikConfig } from 'formik';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useValidationSchema } from '../ProjectModal';

interface FormValues {
  name: string;
  targetOrganisationId: string;
}

const useTranslations = () => {
  const namePlaceholder = useT('enter a project name', { swc: true });
  return { namePlaceholder };
};

interface ProjectCopyToOrganisationModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name'>;
}

// Soft-copy a project into another organisation, tagged as a soft copy: superadmins into any of
// theirs, a partner into its own from an organisation it referred. Not the organisation the project
// is in — that is a duplicate (ProjectDuplicateModal).
const ProjectCopyToOrganisationModal_: React.FC2<ProjectCopyToOrganisationModalProps> = ({ onClose, open, project }) => {
  const validationSchema = useValidationSchema();
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const currentUser = useContext(UserContext);
  const [copyToOrganisation, { loading: isSubmitting }] = useMutation<ProjectCopyToOrganisationMutation, ProjectCopyToOrganisationMutationVariables>(PROJECT_COPY_TO_ORGANISATION);

  const targetOrganisations = useMemo(() => currentUser.organisations.filter((org) => org.id !== organisationId), [currentUser.organisations, organisationId]);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    (values) => {
      copyToOrganisation({
        variables: { projectId: project.id, name: values.name, targetOrganisationId: values.targetOrganisationId },
        onCompleted: onClose,
        refetchQueries: [{ query: PROJECTS, variables: { organisationId: values.targetOrganisationId } }],
        awaitRefetchQueries: true,
      });
    },
    [copyToOrganisation, onClose, project.id],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: { name: project.name, targetOrganisationId: targetOrganisations[0]?.id ?? '' },
      onSubmit,
      validationSchema,
    }),
    [onSubmit, validationSchema, project.name, targetOrganisations],
  );

  return (
    <CreateModal title={<T _str="soft copy '{name}' to organisation" args={{ name: project.name }} swc />} createButtonTitle={<T _str="soft copy" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={isSubmitting} noOverflow>
      <div className="grid grid-cols-1 gap-6 mb-6">
        <FormikInput autoFocus name="name" label={<T _str="name" swc />} placeholder={translations.namePlaceholder} />
        <FormikSelect name="targetOrganisationId" label={<T _str="target organisation" swc />}>
          {targetOrganisations.map((org) => (
            <Option key={org.id} value={org.id}>
              {org.name}
            </Option>
          ))}
        </FormikSelect>
      </div>
    </CreateModal>
  );
};

export const ProjectCopyToOrganisationModal = memo(ProjectCopyToOrganisationModal_);
